import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import eventService from "../../services/eventService";
import campusService from "../../services/campusService";
import clubService from "../../services/clubService";
import universityService from "../../services/universityService";
import { useNotification } from "../../context/NotificationContext";
import AppButton from "../../components/ui/AppButton";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";
import { TypeBadge, ScopeBadge } from "../../components/common/EventDetailModal";

const EventList = ({ onEdit, onView, refreshTrigger }) => {
  const { t } = useTranslation(["events", "common"]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filters
  const [filters, setFilters] = useState({
    universityId: "",
    campusId: "",
    clubId: "",
    status: "",
    eventStatus: "",
  });

  // Filter options
  const [universities, setUniversities] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  const { showSuccess, showError } = useNotification();

  const eventStatuses = ["UPCOMING", "ONGOING", "PAST", "CANCELLED"];

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const filterParams = {
        page: currentPage,
        size: pageSize,
        ...filters,
      };

      const response = await eventService.getEvents(filterParams);

      if (response) {
        setEvents(response.content || []);
        setTotalPages(response.totalPages || 0);
        setTotalElements(response.totalElements || 0);
      }
    } catch (error) {
      showError("Error loading events: " + error);
      console.error("Error loading events:", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters, showError]);

  // Load universities for filter dropdown
  useEffect(() => {
    loadUniversities();
  }, []);

  // Load campuses when university changes
  useEffect(() => {
    if (filters.universityId) {
      loadCampusesByUniversity(filters.universityId);
    } else {
      setCampuses([]);
      setClubs([]);
      setFilters((prev) => ({ ...prev, campusId: "", clubId: "" }));
    }
  }, [filters.universityId]);

  // Load clubs when campus changes
  useEffect(() => {
    if (filters.campusId) {
      loadClubsByCampus(filters.campusId);
    } else {
      setClubs([]);
      setFilters((prev) => ({ ...prev, clubId: "" }));
    }
  }, [filters.campusId]);

  // Load events when filters change
  useEffect(() => {
    loadEvents();
  }, [loadEvents, refreshTrigger]);

  const loadUniversities = async () => {
    try {
      setFiltersLoading(true);
      const response = await universityService.getUniversities(0, 1000);
      if (response.success) {
        setUniversities(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading universities:", error);
    } finally {
      setFiltersLoading(false);
    }
  };

  const loadCampusesByUniversity = async (universityId) => {
    try {
      setFiltersLoading(true);
      const response = await campusService.getCampuses(
        0,
        1000,
        "",
        universityId
      );
      if (response.success) {
        setCampuses(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading campuses:", error);
      setCampuses([]);
    } finally {
      setFiltersLoading(false);
    }
  };

  const loadClubsByCampus = async (campusId) => {
    try {
      setFiltersLoading(true);
      const response = await clubService.getClubs(0, 1000, "", campusId);
      if (response.success) {
        setClubs(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading clubs:", error);
      setClubs([]);
    } finally {
      setFiltersLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setFilters({
      universityId: "",
      campusId: "",
      clubId: "",
      status: "",
      eventStatus: "",
    });
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleDelete = async (eventId) => {
    if (window.confirm(t("events:messages.confirmDelete"))) {
      try {
        await eventService.deleteEvent(eventId);
        showSuccess(t("events:messages.deleteSuccess"));
        loadEvents();
      } catch (error) {
        console.error("Error deleting event:", error);
      }
    }
  };

  const getEventStatusBadge = (status) => {
    const statusConfig = {
      UPCOMING: { color: "primary", icon: "clock" },
      ONGOING: { color: "success", icon: "play" },
      COMPLETED: { color: "info", icon: "check" },
      CANCELLED: { color: "danger", icon: "times" },
      POSTPONED: { color: "warning", icon: "pause" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      icon: "question",
    };

    return (
      <span
        className={`badge bg-${config.color} bg-opacity-10 text-${config.color}`}
      >
        <i className={`fas fa-${config.icon} me-1`}></i>
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <AppButton
          key={i}
          variant={i === currentPage ? "primary" : "outline"}
          className="btn-sm mx-1"
          onClick={() => handlePageChange(i)}
        >
          {i + 1}
        </AppButton>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          {t("common:pagination.showingEntries", {
            start: currentPage * pageSize + 1,
            end: Math.min((currentPage + 1) * pageSize, totalElements),
            total: totalElements,
          })}
        </div>
        <div className="d-flex align-items-center">
          <AppButton
            variant="outline"
            className="btn-sm me-2"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {t("common:buttons.previous")}
          </AppButton>
          {pages}
          <AppButton
            variant="outline"
            className="btn-sm ms-2"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
          >
            {t("common:buttons.next")}
          </AppButton>
        </div>
      </div>
    );
  };

  const hasActiveFilters = Object.values(filters).some((value) => value !== "");

  return (
    <div className="event-list">
      <ListToolbar
        title={t("events:title")}
        subtitle={t("events:list.found", { count: totalElements })}
        filterBadges={
          hasActiveFilters ? (
            <>
              {filters.universityId && (
                <span className="badge bg-info">
                  {t("events:filters.university")}:{" "}
                  {
                    universities.find(
                      (u) => u.id.toString() === filters.universityId
                    )?.nameEn
                  }
                </span>
              )}
              {filters.campusId && (
                <span className="badge bg-success">
                  {t("events:filters.campus")}:{" "}
                  {
                    campuses.find((c) => c.id.toString() === filters.campusId)
                      ?.nameEn
                  }
                </span>
              )}
              {filters.clubId && (
                <span className="badge bg-primary">
                  {t("events:filters.club")}:{" "}
                  {clubs.find((c) => c.id.toString() === filters.clubId)?.name}
                </span>
              )}
              {filters.eventStatus && (
                <span className="badge bg-primary">
                  {t("events:filters.eventStatus")}: {filters.eventStatus}
                </span>
              )}
              {filters.status !== "" && (
                <span className="badge bg-warning">
                  {t("events:filters.activeStatus")}: {filters.status === "true" ? t("common:common.yes") : t("common:common.no")}
                </span>
              )}
            </>
          ) : null
        }
      >
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label fw-medium">{t("events:filters.university")}</label>
            <select
              className="form-select form-select-sm"
              value={filters.universityId}
              onChange={(e) =>
                handleFilterChange("universityId", e.target.value)
              }
              disabled={filtersLoading}
            >
              <option value="">{t("events:filters.allUniversities")}</option>
              {universities.map((university) => (
                <option key={university.id} value={university.id}>
                  {university.nameEn}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label fw-medium">{t("events:filters.campus")}</label>
            <select
              className="form-select form-select-sm"
              value={filters.campusId}
              onChange={(e) => handleFilterChange("campusId", e.target.value)}
              disabled={filtersLoading || !filters.universityId}
            >
              <option value="">
                {filters.universityId
                  ? t("events:filters.allCampuses")
                  : t("events:filters.selectUniversityFirst")}
              </option>
              {campuses.map((campus) => (
                <option key={campus.id} value={campus.id}>
                  {campus.nameEn}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label fw-medium">{t("events:filters.club")}</label>
            <select
              className="form-select form-select-sm"
              value={filters.clubId}
              onChange={(e) => handleFilterChange("clubId", e.target.value)}
              disabled={filtersLoading || !filters.campusId}
            >
              <option value="">
                {filters.campusId ? t("events:filters.allClubs") : t("events:filters.selectCampusFirst")}
              </option>
              {clubs.map((club) => (
                <option key={club.id} value={club.id}>
                  {club.nameEn || club.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <div className="row">
              <div className="col-md-6">
                <label className="form-label fw-medium">{t("events:filters.eventStatus")}</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.eventStatus}
                  onChange={(e) =>
                    handleFilterChange("eventStatus", e.target.value)
                  }
                >
                  <option value="">{t("events:filters.allStatuses")}</option>
                  {eventStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-medium">{t("events:filters.activeStatus")}</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.status}
                  onChange={(e) =>
                    handleFilterChange("status", e.target.value)
                  }
                >
                  <option value="">{t("common:common.all")}</option>
                  <option value="true">{t("common:status.active")}</option>
                  <option value="false">{t("common:status.inactive")}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">{t("common:pagination.showing")}:</label>
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={handlePageSizeChange}
                style={{ width: "auto" }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="col-auto d-flex align-items-end">
              <AppButton variant="outline" className="btn-sm" onClick={clearFilters}>
                <i className="fas fa-times me-2"></i>
                {t("common:buttons.clearFilters")}
              </AppButton>
            </div>
          )}
        </div>
      </ListToolbar>

      {/* Events Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("common:actions.loading")}</span>
              </div>
              <p className="text-muted mt-2">{t("events:list.loading")}</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="fas fa-calendar-alt text-muted mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="text-muted">{t("events:list.noFound")}</h5>
              <p className="text-muted">
                {hasActiveFilters
                  ? t("events:list.tryAdjustFilters")
                  : t("events:list.noneCreated")}
              </p>
            </div>
          ) : (
            <AppTable responsive>
                <thead>
                  <tr>
                    <th className="ps-4">{t("events:table.event")}</th>
                    <th>{t("events:table.clubLocation")}</th>
                    <th className="text-center">{t("events:table.type")}</th>
                    <th className="text-center">{t("events:table.scope")}</th>
                    <th className="text-center">{t("events:fields.status")}</th>
                    <th className="text-center">{t("common:status.active")}</th>
                    <th>{t("events:table.startDate")}</th>
                    <th className="text-center pe-4">{t("common:table.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id} className="border-bottom">
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          <div
                            className="event-icon rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-3"
                            style={{ width: "45px", height: "45px" }}
                          >
                            <i className="fas fa-calendar-alt text-primary"></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {event.name || event.title}
                            </div>
                            <small className="text-muted">ID: {event.id}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <div className="fw-medium text-dark">
                            {event.clubName}
                          </div>
                          <small className="text-muted">
                            {event.campusName}, {event.universityName}
                          </small>
                        </div>
                      </td>
                      <td className="text-center">
                        <TypeBadge type={event.participationType || "PHYSICAL"} />
                      </td>
                      <td className="text-center">
                        <ScopeBadge scope={event.participationScope || "PUBLIC"} />
                      </td>
                      <td className="text-center">
                        {getEventStatusBadge(event.eventStatus)}
                      </td>
                      <td className="text-center">
                        {event.status ? (
                          <i
                            className="fas fa-check-circle text-success status-icon"
                            title={t("common:status.active")}
                          ></i>
                        ) : (
                          <i
                            className="fas fa-times-circle text-danger status-icon"
                            title={t("common:status.inactive")}
                          ></i>
                        )}
                      </td>
                      <td>
                        <span className="text-muted">
                          {formatDate(event.startDate)}
                        </span>
                      </td>
                      <td className="text-center pe-4">
                        <div className="btn-group" role="group">
                          <AppButton
                            variant="outline"
                            className="btn-sm text-info border-info"
                            onClick={() => onView(event)}
                            title={t("common:buttons.viewDetails")}
                          >
                            <i className="fas fa-eye"></i>
                          </AppButton>
                          <AppButton
                            variant="outline"
                            className="btn-sm text-primary border-primary"
                            onClick={() => onEdit(event)}
                            title={t("events:editEvent")}
                          >
                            <i className="fas fa-edit"></i>
                          </AppButton>
                          <AppButton
                            variant="outline"
                            className="btn-sm text-danger border-danger"
                            onClick={() => handleDelete(event.id)}
                            title={t("common:buttons.delete")}
                          >
                            <i className="fas fa-trash"></i>
                          </AppButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
            </AppTable>
          )}
        </div>
      </div>

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default EventList;
